// VgmSynth — binds a parsed VGM to a real core's APU and pulls PCM out.
// This is the one place the chip-agnostic player (./vgm-player.ts) meets a
// concrete AudioSynthCapable core: the core is the ChipSink, its
// writeAudioRegister/synthSamples drive the chip, and drainAudio() hands
// back the interleaved-stereo Float32 the player's waits produced.
//
// A VgmSynth owns a dedicated synth core instance (constructed with
// emulateSound: true, renderPixels: false). It never shares the game's
// core — in an overlay the game runs in its own instance and the two
// audio streams mix (see mixInto).
import { VGM_SAMPLE_RATE } from "./vgm.js";
import { VgmPlayer } from "./vgm-player.js";
export class VgmSynth {
    core;
    player;
    // Output-rate ÷ VGM rate. VGM waits are clocked at 44.1 kHz; the synth
    // core renders at its own sampleRate (48 kHz in the browser). Without
    // this conversion the player would treat 48 kHz output samples as 44.1
    // kHz wait-samples and play everything ~8.8% fast. `sampleRate` defaults
    // to the VGM rate (ratio 1 = no conversion) for headless callers that
    // don't care about absolute pitch.
    rateRatio;
    constructor(file, core, opts = {}, sampleRate = VGM_SAMPLE_RATE) {
        this.core = core;
        this.rateRatio = sampleRate / VGM_SAMPLE_RATE;
        const sink = {
            chips: core.audioChips(),
            writeRegister: (_chip, reg, value) => {
                // Single-chip core: the player has already gated `_chip` to one of
                // core.audioChips(), so the offset maps straight onto this chip.
                core.writeAudioRegister(reg, value);
            },
            advance: (vgmSamples) => {
                // The player advances in VGM (44.1 kHz) sample units; the core
                // produces output-rate samples.
                core.synthSamples(Math.round(vgmSamples * this.rateRatio));
            },
        };
        this.player = new VgmPlayer(file, sink, opts);
    }
    // True when the file targets a chip this core can't synthesize — it will
    // render silence. Callers surface this as a diagnostic.
    get playable() {
        return this.player.playable;
    }
    // True once a one-shot has played out.
    get finished() {
        return this.player.finished;
    }
    reset() {
        this.player.reset();
        // Flush any audio still buffered in the core from the prior run.
        this.core.drainAudio();
    }
    // Produce `samples` output samples and return them as interleaved
    // stereo Float32 (L=R for these mono chips), or null if nothing was
    // produced. `samples` is per channel — the returned array is up to
    // 2*samples long (the core decides the exact count).
    render(samples) {
        // `samples` is in output-rate units; the player counts VGM (44.1 kHz)
        // wait-samples, so advance it by the equivalent VGM span.
        this.player.render(Math.round(samples / this.rateRatio));
        return this.core.drainAudio();
    }
}
// Find the active (non-silent) frame span of a VGM by replaying it through
// a synth core one frame at a time and measuring each frame's AC amplitude
// (peak-to-peak — DC-invariant, since the APU output sits on a constant
// offset and silence reads as a flat line). Returns the first/last frames
// belonging to a run of activity, or null if the whole thing is silent. Used
// to auto-trim captured SFX (docs/sound.md §"Auto-trim"). `frameCount` bounds
// the scan (one render == one frame at `sampleRate`).
//
// A run must hold >= `minRun` active frames but they need NOT be consecutive:
// short internal silences (up to `maxGap` frames, see below) are bridged. Two
// things force this:
//   - Intermittent SFX. SMB's brick-shatter is a crackle whose noise channel
//     goes silent every OTHER frame (its length counter expires between
//     reloads), so it has NO two consecutive active frames. A consecutive-run
//     detector returns null for it, and the caller then ships the capture
//     UNTRIMMED (leading + trailing dead air intact) — the exact bug this fixes.
//   - The frame-0 prime blip. The primed channel state (VgmRecorder.audio-
//     PrimeState) plus the game's per-frame idle writes can re-key an idle
//     triangle at an ultrasonic period, ticking the synth's DAC/high-pass for a
//     SINGLE frame. It sits alone, many frames (a human's trigger reaction
//     time) before the real onset — far beyond maxGap — so it never bridges in,
//     and as a 1-active-frame run it fails minRun and is discarded. (The prime
//     writes still survive into the output — they're needed so a channel enabled
//     before Record isn't muted on replay — see VgmRecorder.start.)
export function measureActiveFrameSpan(file, core, sampleRate, frameCount, threshold = 0.01, minRun = 2) {
    const synth = new VgmSynth(file, core, { loop: false }, sampleRate);
    synth.reset();
    const perFrame = Math.max(1, Math.round(sampleRate / 60));
    const active = [];
    for (let f = 0; f < frameCount; f++) {
        const out = synth.render(perFrame);
        let lo = Infinity;
        let hi = -Infinity;
        if (out) {
            for (let i = 0; i < out.length; i += 2) {
                const s = out[i];
                if (s < lo)
                    lo = s;
                if (s > hi)
                    hi = s;
            }
        }
        const ptp = out && out.length ? hi - lo : 0;
        active.push(ptp > threshold);
    }
    // Anchor first/last on a GAP-TOLERANT run of active frames, discarding
    // isolated blips shorter than `minRun` active frames. Bridging is load-
    // bearing: many SFX are intermittent — SMB's brick-shatter is a crackle that
    // falls silent every OTHER frame as its noise length counter expires between
    // reloads, so no two active frames are ever consecutive. A consecutive-run
    // detector finds no qualifying run and returns null, which silently disables
    // the caller's auto-trim (it ships the untrimmed capture, dead air and all).
    // We instead bridge internal silences up to `maxGap` frames into one run,
    // then require >= `minRun` ACTIVE frames in it. That still drops the lone
    // frame-0 prime-state blip: it sits alone, many frames (a human's trigger
    // reaction time) before the real onset — far beyond maxGap — so it never
    // bridges into the sound and, as a 1-active-frame run, fails minRun.
    const maxGap = 4;
    let first = -1;
    let last = -1;
    let runStart = -1; // first active frame of the current run
    let lastActive = -1; // most recent active frame in the current run
    let activeCount = 0; // active frames accumulated in the current run
    for (let f = 0; f <= frameCount; f++) {
        if (f < frameCount && active[f]) {
            if (runStart < 0) {
                runStart = f;
                activeCount = 0;
            }
            lastActive = f;
            activeCount++;
        }
        else if (runStart >= 0 && (f === frameCount || f - lastActive > maxGap)) {
            // Run ends: a silence longer than maxGap, or the end of the capture.
            if (activeCount >= minRun) {
                if (first < 0)
                    first = runStart;
                last = lastActive;
            }
            runStart = -1;
            activeCount = 0;
        }
    }
    return first < 0 ? null : { first, last };
}
// Sum `add` into `base` in place, extending with a copy when `add` is
// longer. Used to mix the synth stream onto the game's audio buffer (both
// interleaved stereo at the same sample rate). Output is soft-clamped to
// [-1, 1] so a loud overlay can't wrap.
export function mixInto(base, add) {
    if (add === null)
        return base;
    if (base === null)
        return add;
    const n = Math.max(base.length, add.length);
    const out = base.length >= n ? base : new Float32Array(n);
    if (out !== base)
        out.set(base);
    for (let i = 0; i < add.length; i++) {
        let v = out[i] + add[i];
        if (v > 1)
            v = 1;
        else if (v < -1)
            v = -1;
        out[i] = v;
    }
    return out;
}
//# sourceMappingURL=vgm-synth.js.map